import { TableBadge } from "@/components/ui";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import {
  type ReportsMatterSearchTableInsuranceCoverage,
  type ReportsMatterSearchTableInsuranceLiability,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";

const UNKNOWN_CONTACT_LABEL = "No Provider";

const normalizeText = (value?: string | null) => value?.trim() || undefined;

const formatContactName = (value?: string | null) =>
  normalizeText(value) ?? UNKNOWN_CONTACT_LABEL;

const formatLiabilityPercentage = (value?: number | null) => {
  if (value == null || Number.isNaN(value)) return undefined;

  return `${new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(value)}%`;
};

const insuranceBadgeClassName = (status?: string | null) => {
  switch (normalizeText(status)) {
    case "Accepted":
    case "Confirmed":
      return "bg-emerald-100 text-emerald-700";
    case "Denied":
      return "bg-rose-100 text-rose-700";
    case "Pending":
      return "bg-amber-100 text-amber-700";
    case "Unconfirmed":
      return "bg-slate-100 text-slate-700";
    default:
      return "bg-slate-100 text-slate-700";
  }
};

export const formatInsuranceCoverageValue = (
  item: ReportsMatterSearchTableInsuranceCoverage,
) => {
  const coverage = normalizeText(item.coverage);
  return `${formatContactName(item.matterContactConnectionContactName)}: ${
    coverage ?? "-"
  }`;
};

export const formatInsuranceLiabilityValue = (
  item: ReportsMatterSearchTableInsuranceLiability,
) => {
  const liability = normalizeText(item.liability);
  const percentage = formatLiabilityPercentage(item.liabilityPercentage);

  return `${formatContactName(item.matterContactConnectionContactName)}: ${
    liability ?? "-"
  }${percentage ? ` (${percentage})` : ""}`;
};

type MatterInsuranceReportBadgeProps =
  | {
      type: "coverage";
      items?: ReportsMatterSearchTableInsuranceCoverage[] | null;
    }
  | {
      type: "liability";
      items?: ReportsMatterSearchTableInsuranceLiability[] | null;
    };

type MatterInsuranceReportBadgeBaseProps = {
  badgeLabel: string;
  tooltipMessage: string;
  status?: string | null;
};

function MatterInsuranceReportBadgeBase({
  badgeLabel,
  tooltipMessage,
  status,
}: MatterInsuranceReportBadgeBaseProps) {
  return (
    <TooltipWrapper message={tooltipMessage}>
      <div className="inline-flex max-w-full">
        <TableBadge
          className={cn(
            "max-w-[220px] cursor-default justify-start truncate",
            insuranceBadgeClassName(status),
          )}
        >
          <span className="truncate">{badgeLabel}</span>
        </TableBadge>
      </div>
    </TooltipWrapper>
  );
}

export function MatterInsuranceReportBadge(
  props: MatterInsuranceReportBadgeProps,
) {
  if (props.type === "coverage") {
    const items = props.items ?? [];
    if (!items.length) return "-";

    const firstStatus = normalizeText(items[0]?.coverage);
    const badgeLabel = `${formatInsuranceCoverageValue(items[0])}${
      items.length > 1 ? ` +${items.length - 1}` : ""
    }`;

    return (
      <MatterInsuranceReportBadgeBase
        badgeLabel={badgeLabel}
        tooltipMessage={items.map(formatInsuranceCoverageValue).join("\n")}
        status={firstStatus}
      />
    );
  }

  const items = props.items ?? [];
  if (!items.length) return "-";

  const firstStatus = normalizeText(items[0]?.liability);
  const badgeLabel = `${formatInsuranceLiabilityValue(items[0])}${
    items.length > 1 ? ` +${items.length - 1}` : ""
  }`;

  return (
    <MatterInsuranceReportBadgeBase
      badgeLabel={badgeLabel}
      tooltipMessage={items.map(formatInsuranceLiabilityValue).join("\n")}
      status={firstStatus}
    />
  );
}
